import { useMemo } from 'react'
import { isFuture, isToday, isYesterday, startOfDay } from 'date-fns'
import { DateTime } from 'luxon'
import { useTranslate } from '@/utils/translate/translate-client'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { CurrentTheatricalRelease, ReleaseActionType } from '@/views/TicketsLandingView/types'
import { getTodaysUserTicketsBySlug, isInTheaters } from '@/views/TicketsLandingView/utils'

export const useGetHeroRelease = (releases: CurrentTheatricalRelease[], userTickets: SimpleTicketItem[]) => {
  return useMemo(() => {
    const heroRelease = releases.find((release) => release.isFeatureRelease) ?? releases[0]
    return {
      heroRelease,
      heroUserTickets: getTodaysUserTicketsBySlug(userTickets, heroRelease?.theatricalSlug ?? ''),
    }
  }, [releases, userTickets])
}

export const useGetFilteredHeroRelease = (releases: CurrentTheatricalRelease[], userTickets: SimpleTicketItem[]) => {
  return useMemo(() => {
    const heroRelease = releases.find((release) => release.isFeatureRelease) ?? releases[0]

    return {
      heroRelease,
      heroUserTickets: getTodaysUserTicketsBySlug(userTickets, heroRelease?.theatricalSlug ?? ''),
    }
  }, [releases, userTickets])
}

export const useGetFeaturedRelease = (releases: CurrentTheatricalRelease[]) => {
  return useMemo(() => {
    return {
      featuredRelease: releases.find((release) => release.isFeatureRelease) ?? releases[0],
    }
  }, [releases])
}

export const useGetReleasesWithGuildDiscounts = (releases: CurrentTheatricalRelease[]) => {
  return useMemo(() => {
    return {
      releasesWithGuildDiscounts:
        releases.filter((release) => !!release?.guildCodes && release.guildCodes.length > 0) ?? [],
    }
  }, [releases])
}

export const useReleaseDateString = (releaseDate: string, locale: string) => {
  const { t } = useTranslate('tickets')

  return useMemo(() => {
    const monthDayString = DateTime.fromISO(releaseDate, { zone: 'utc', locale }).toFormat('MMMM d')
    return t('inTheatersMonthDay', 'In Theaters {{monthDayString}}', { monthDayString })
  }, [locale, releaseDate, t])
}

export const useInTheatersLabel = (release: CurrentTheatricalRelease, locale: string) => {
  const { t } = useTranslate('tickets')
  const monthDayString = DateTime.fromISO(release.releaseDate, { zone: 'utc', locale }).toFormat('MMMM d')

  return isInTheaters(release)
    ? t('inTheatersNow', 'In Theaters Now')
    : t('inTheatersMonthDay', 'In Theaters {{monthDayString}}', { monthDayString })
}

export const useIsInTheatersNow = ({ releaseDate, ticketStatus }: CurrentTheatricalRelease) => {
  return useMemo(() => {
    if (ticketStatus === 'LIVE') return true

    return isYesterday(startOfDay(releaseDate)) || isToday(startOfDay(releaseDate) || isFuture(startOfDay(releaseDate)))
  }, [releaseDate, ticketStatus])
}

export const useGetReleaseActionType = (
  release: CurrentTheatricalRelease,
  ticketForToday?: SimpleTicketItem[],
): ReleaseActionType => {
  const isInTheatersNow = useIsInTheatersNow(release)

  return useMemo(() => {
    const hasGuildCodes = release?.guildCodes && release.guildCodes.length > 0
    const hasActiveGiveaway = release.activeSweepstake
    const isPresaleAvailable = release.ticketStatus === 'PRE_SALES'

    if (ticketForToday && ticketForToday.length > 0) return 'TICKETS_TODAY'
    if (hasGuildCodes && (isInTheatersNow || isPresaleAvailable)) return 'GUILD_TICKETS'
    if (isInTheatersNow) return 'SHOWTIMES'
    if (isPresaleAvailable) return 'PRESALE'
    if (hasActiveGiveaway) return 'GIVEAWAY'
    return 'NONE'
  }, [isInTheatersNow, release.activeSweepstake, release.guildCodes, release.ticketStatus, ticketForToday])
}

export const useGetShareInfo = (release: CurrentTheatricalRelease) => {
  const { t } = useTranslate('tickets')
  const { ticketStatus, title, activeSweepstake, theatricalSlug } = release

  return useMemo(() => {
    if (ticketStatus === 'PRE_SALES' || ticketStatus === 'LIVE') {
      return {
        shareUrl: `https://www.angel.com/tickets/${theatricalSlug}`,
        title: t('ticketsSEOTitleAngel', `{{theatricalName}} Tickets & Showtimes | Angel`, {
          theatricalName: title,
        }),
        message: t(
          'ticketsSEODescription',
          'Buy {{theatricalName}} tickets, watch trailers and find showtimes near you.',
          {
            theatricalName: title,
          },
        ),
      }
    }

    if (activeSweepstake) {
      return {
        shareUrl: `https://www.angel.com/giveaway/${theatricalSlug}`,
        title: t('giveawaySeoTitleAngel', 'PRE-ORDER {{projectName}} Tickets | Angel', {
          projectName: title,
        }),
        message: t('giveawaySeoDescription', 'Buy {{projectName}} tickets for a Chance to Win the Giveaway!', {
          projectName: title,
        }),
      }
    }

    return {
      shareUrl: `https://www.angel.com/tickets`,
      title: t('ticketLandingPageTitleAngel', 'Tickets & Showtimes | In Theaters | Angel'),
      message: t(
        'ticketsLandingPageDescription',
        "Buy tickets and get showtimes for Angel Originals at a theater near you today. See what's currently in theaters or explore upcoming and past releases. Angel Guild members get 2 tickets for every theatrical release as part of their membership.",
      ),
    }
  }, [activeSweepstake, t, theatricalSlug, ticketStatus, title])
}
